/**
 * How the setup guide decides it is finished.
 *
 * Extracted from the hook because the hook is five network probes behind four
 * React providers, and this is the part that was wrong: a step the reader has
 * no scope for answered `done: false, loading: false` forever, so the guide's
 * completion never tripped and a plain Reader was pinned to a checklist of
 * things they are not allowed to do — instead of the queue, which is the whole
 * point of the landing page.
 *
 * The rule is one sentence: a step nobody can do is not a step anybody owes.
 */

/** The fields of an onboarding step this rule reads. */
export interface StepState {
  done: boolean;
  loading: boolean;
  /** The reader has no scope for it, so it can be neither checked nor done. */
  unavailable: boolean;
  /**
   * Its probe came back an error, so we cannot say whether it is done.
   *
   * Distinct from `!done` in the way that matters: one 5xx on `/teams` used to
   * turn a fully set-up tenant's front door into "Getting started — Invite your
   * team", which is a claim about their workspace made because a request
   * failed.
   */
  failed?: boolean;
  /**
   * Offered, but not owed — it does not gate completion.
   *
   * The wallet step is the case. Four of the five steps configure the TENANT;
   * connecting a browser wallet configures this browser, and a workspace that
   * is fully set up is not incomplete because nobody plugged in a wallet. Left
   * required, it made `allComplete` unreachable for any tenant that never
   * connected one — so the queue was never the default landing page, which
   * defeated the whole point of building it.
   */
  optional?: boolean;
}

export interface Completion {
  /** How many of the reader's OWN required steps are done. */
  completedCount: number;
  /** How many required steps are the reader's at all. */
  total: number;
  /**
   * Nothing required is left for this reader — including when nothing ever was.
   *
   * False while `unknown` is true: not knowing is not the same as knowing there
   * is work left, and the caller needs to tell those apart.
   */
  allComplete: boolean;
  /** Any probe still resolving, available or not. */
  isLoading: boolean;
  /**
   * A required probe failed, so completion cannot be computed.
   *
   * The caller decides what to do with this, and the asymmetry is the whole
   * point: wrongly showing a setup checklist to an established tenant HIDES
   * their work, while wrongly showing the queue to a new tenant costs them one
   * click on "Getting started". So an unknown answer should fall towards the
   * queue, never towards the guide.
   */
  unknown: boolean;
}

/** The steps that are this reader's to do at all. */
export function ownSteps<T extends StepState>(steps: readonly T[]): T[] {
  return steps.filter((step) => !step.unavailable);
}

/** The steps that decide whether setup is finished. */
export function requiredSteps<T extends StepState>(steps: readonly T[]): T[] {
  return ownSteps(steps).filter((step) => !step.optional);
}

/** {@link Completion} over `steps`. */
export function completion(steps: readonly StepState[]): Completion {
  const required = requiredSteps(steps);
  const completedCount = required.filter((step) => step.done).length;
  // Over EVERY step, not just the reader's: an in-flight probe must hold the
  // decision back, or a set-up tenant flashes the guide for one paint.
  const isLoading = steps.some((step) => step.loading);
  const unknown = required.some((step) => step.failed === true);
  return {
    completedCount,
    total: required.length,
    allComplete: !isLoading && !unknown && completedCount === required.length,
    isLoading,
    unknown,
  };
}

/** The fields of an org member (`OrgMember`, api/orgUsersSource) the team rule reads. */
export interface OrgMemberState {
  /** `active` | `pending_invitation` | `suspended` | `inactive` (case as sent). */
  status: string;
  /** An email-keyed invitation nobody has accepted yet. */
  pending: boolean;
}

/** Statuses of a member who has been removed from the org: not a teammate any more. */
const REMOVED_MEMBER_STATUSES: ReadonlySet<string> = new Set(["inactive", "disabled"]);

/**
 * "Invite your team" is done once somebody besides the reader is on it: more
 * than one current org member, or at least one invitation still outstanding.
 *
 * It used to be "at least one team exists" (`GET /teams`), which is always true
 * — seed-identity creates a default team for every new org — so the step was
 * ticked for a customer who had never invited anyone. A removed (`inactive`)
 * member no longer counts; a pending invite does, because inviting is the step.
 */
export function hasInvitedTeam(members: readonly OrgMemberState[]): boolean {
  if (members.some((member) => member.pending)) return true;
  const current = members.filter(
    (member) => !REMOVED_MEMBER_STATUSES.has(member.status.toLowerCase()),
  );
  return current.length > 1;
}
